import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";

import { select, Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { createPos, createVPos, updatePos, updateVPos } from "src/app/store/actions/sites/sites.actions";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { SiteMultimediaDTO } from "src/app/models/Sites/siteMultimediaDTO";
import { SitePeripheralDTO } from "src/app/models/Sites/sitePeripheralDTO";
import { PosType, SitePosDTO, VirtualPosDTO } from "src/app/models/Sites/sitePosDTO";
import { DefaultConfirmationDialogData, Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { CategoriesAndPropsUtils } from "src/app/shared/utils/categoriesAndPropsUtils";
import { debounceTime, filter, map, startWith, switchMap, take, takeUntil } from "rxjs/operators";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";

const POS_FORM_PARAMS = {
  code: "code",
  enabled: "enabled",
  posNumber: "posNumber",
  serialNumber: "serialNumber",
  description: "description",
  connectedFuelPoints: "connectedFuelPoints",
  connectedPeripherals: "connectedPeripherals",
  connectedMultimedia: "connectedMultimedia",
  protocolTypeCode: "protocolTypeCode",
  protocol: "protocol",
  virtualPos: "virtualPos",
  connectedPumps: "connectedPumps",
  posProperties: "posProperties",
  properties: "properties", //vpos properties
  siteCode: "siteCode",
  posTypeCode: "posTypeCode",
  vPosEnabled: "vPosEnabled",
  deviceProxyIp: "deviceProxyIp",
  deviceProxyPort: "deviceProxyPort",
  maxTransactionAttMode: "maxTransactionAttMode",
  maxTransactionFp: "maxTransactionFp",
  printSaleReceipt: "printSaleReceipt",
  showAfterSale: "showAfterSale",
  showKeyboard: "showKeyboard",
  printShift: "printShift",
  dailyAllowsUnpaidTransactionWhenClose: "dailyAllowsUnpaidTransactionWhenClose",
  additionalData: "additionalData",
  optType: "optType",
  calculatorWebsocketUrl: "calculatorWebsocketUrl",
  posManagerWebsocketUrl: "posManagerWebsocketUrl",
  axonMultimediaIframe: "axonMultimediaIframe",
  axonMultimediaUrl: "axonMultimediaUrl", 
  calculatorTimeout: "calculatorTimeout", 
  clock: "clock",
  debug: "debug",
  locale: "locale", 
  productUnit: "productUnit",
  themeId: "themeId", 
  background: "background",
  title: "title",
  video: "video",
  welcomeMessage: "welcomeMessage",
  calculatorSides: "calculatorSides",
};

const DEFAULT_PORT: string = '51076';
const DEFAULT_POS_MANAGER_API_URL: string = 'http://localhost:51028';

@Component({
  selector: "app-pos-form-dialog",
  templateUrl: "./pos-form-dialog.component.html",
  styleUrls: ["./pos-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PosFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getPosForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  protected destroy$ = new Subject<void>();

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;

  siteCode: string;
  currentPos: SitePosDTO;

  fuelPointList: SiteFuelPointDTO[];
  peripheralList: SitePeripheralDTO[];
  multimediaList: SiteMultimediaDTO[];
  propertyList: PropertyDTO[];

  isCreate: boolean = false;

  posDataForm: FormGroup;

  tabs = ["POS"];
  selectedTab = new FormControl(0);

  public vposPosTypeList: PosType[];
  public sellingPointPosTypeList: PosType[];

  optTypes = ["DIT", "PEDESTAL"];
  locales = [
    { code: "en-en", label: "Languages.English" },
    { code: "pt-pt", label: "Languages.Portuguese" },
    { code: "es-es", label: "Languages.Spanish" },
    { code: "fr-FR", label: "Languages.French" },
    { code: "de-DE", label: "Languages.German" },
    { code: "nl-NL", label: "Languages.Dutch" },
  ];
  idleScreens = [
    { code: "green", label: "Tenant.green" },
    { code: "blue", label: "Tenant.blue" },
    { code: "red", label: "Tenant.red" },
    { code: "violet", label: "Tenant.violet" },
  ];
  themes = [
    { code: "dark", label: "Tenant.dark" },
    { code: "light", label: "Tenant.light" },
  ];
  calculatorSides = ["1-2", "2-1"];

  searchTerm = '';
  tabSearchControl = new FormControl('');
  currentFilteredTabs: {index: number; label: string}[]=[];
  filteredTabs: Observable <{index: number, label: string}[]>

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PosFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    public rolePermissionsService: RolePermissionsService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createPosForm();
    this.setFormValues(this.currentPos);
    this.setVPosFormArray(this.currentPos);
    
    this.filteredTabs = this.tabSearchControl.valueChanges.pipe(
      startWith(''),
      map(term => this.filterTabsByNumber(term))
    );

    this.filteredTabs.subscribe(tabs => {
      this.currentFilteredTabs = tabs;
    });
  }

  private filterTabsByNumber(term: string): { index: number; label: string }[] {
    const searchTerm = (term || '').toString().trim();

    const allTabs = this.tabs.map((tab, i) => ({
      index: i,
      label: this.getTabNumber(i)
    }));

    if (!searchTerm) {
      return allTabs;
    }

    const vposMatches = allTabs
      .filter(t => t.label.includes(searchTerm));

    return [...vposMatches];
  }

  getTabNumber(index: number): string {
    if (index === 0) {
      return `${this.posDataForm.get('posNumber')?.value ?? '--'}`;
    }
    return `${this.getVposNr(index - 1)}`;
  }

  selectFirstPosSearch(): void {
    const firstVposMatch = this.currentFilteredTabs.find(t => t.index > 0);
    if (firstVposMatch) {
      this.selectedTab.setValue(firstVposMatch.index);
    }
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentPos = this.data.currentPos;
    this.fuelPointList = this.data.fuelPointList;
    this.peripheralList = this.data.peripheralList;
    this.multimediaList = this.data.multimediaList;
    this.sellingPointPosTypeList = this.data.posTypeList.filter(
      (pos) => pos.code !== "BACKOFFICE"
    );
    this.vposPosTypeList = this.data.posTypeList.filter(
      (pos) => pos.code !== "BACKOFFICE" && pos.code != "POS_MANAGER"
    );
    this.propertyList = this.data.propertyList;
  }

  createPosForm() {
    this.posDataForm = this.fb.group({
      [POS_FORM_PARAMS.enabled]: [true],
      [POS_FORM_PARAMS.posNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [POS_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [POS_FORM_PARAMS.description]: [
        null,
        [Validators.required, Validators.maxLength(45)],
      ],
      [POS_FORM_PARAMS.connectedFuelPoints]: [null],
      [POS_FORM_PARAMS.connectedPeripherals]: [null],
      [POS_FORM_PARAMS.connectedMultimedia]: [null],
      [POS_FORM_PARAMS.protocolTypeCode]: [null, [Validators.required]],
      [POS_FORM_PARAMS.protocol]: this.fb.group({}),
      [POS_FORM_PARAMS.virtualPos]: this.fb.array([]),
      [POS_FORM_PARAMS.posProperties]: this.fb.array([]),
      [POS_FORM_PARAMS.deviceProxyIp]: [null],
      [POS_FORM_PARAMS.deviceProxyPort]: [null],
      [POS_FORM_PARAMS.maxTransactionFp]: [
        null,
        [Validators.pattern("[0-9]+")],
      ],
      [POS_FORM_PARAMS.maxTransactionAttMode]: [
        null,
        [Validators.pattern("[0-9]+")],
      ],
      [POS_FORM_PARAMS.optType]: [null],
      [POS_FORM_PARAMS.calculatorWebsocketUrl]: [null],
      [POS_FORM_PARAMS.posManagerWebsocketUrl]: [null],
      [POS_FORM_PARAMS.axonMultimediaIframe]: [null],
      [POS_FORM_PARAMS.axonMultimediaUrl]: [null],
      [POS_FORM_PARAMS.calculatorTimeout]: [null],
      [POS_FORM_PARAMS.clock]: [null],
      [POS_FORM_PARAMS.debug]: [null],
      [POS_FORM_PARAMS.locale]: [null],
      [POS_FORM_PARAMS.productUnit]: [null],
      [POS_FORM_PARAMS.themeId]: [null],
      [POS_FORM_PARAMS.background]: [null],
      [POS_FORM_PARAMS.title]: [null],
      [POS_FORM_PARAMS.video]: [null],
      [POS_FORM_PARAMS.welcomeMessage]: [null],
      [POS_FORM_PARAMS.calculatorSides]: [null],
    });
    this.subscribeFormChanges(this.posDataForm);
    this.checkPermissions();
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission("SITE_MANAGEMENT", "WRITE")
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if (!canEdit) {
          this.setFormValues(this.currentPos);
          setTimeout(() => {
            this.disableFormControls(this.posDataForm);
          });
        }
      });

    this.canWrite$ = this.rolePermissionsService.hasPermission(
      "SITE_MANAGEMENT",
      "WRITE"
    );
  }

  protected disableFormControls(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        this.disableFormControls(control); // Desativa filhos primeiro
      }

      control?.disable(); // Desativa o próprio
    });
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getPosPristine();
    });
  }

  setFormValues(pos: SitePosDTO, duplication?: boolean) {
    this.posDataForm.controls.enabled.setValue(pos?.enabled ?? false);
    this.posDataForm.controls.posNumber.setValue(
      duplication ? null : pos?.posNumber ?? null
    );
    this.posDataForm.controls.serialNumber.setValue(
      duplication ? null : pos?.serialNumber ?? null
    );
    this.posDataForm.controls.description.setValue(pos?.description ?? null);
    this.posDataForm.controls.protocolTypeCode.setValue(
      pos?.protocolTypeCode ?? null
    );
    this.posDataForm.controls.connectedFuelPoints.setValue(
      pos?.connectedFuelPoints ?? null
    );
    this.posDataForm.controls.connectedPeripherals.setValue(
      pos?.connectedPeripherals ?? null
    );
    this.posDataForm.controls.connectedMultimedia.setValue(
      pos?.connectedMultimedia ?? null
    );
    this.posDataForm.controls.deviceProxyIp.setValue(
      pos?.additionalData?.deviceProxy?.ip ?? null
    );
    this.posDataForm.controls.deviceProxyPort.setValue(
      pos?.additionalData?.deviceProxy?.port ?? null
    );
    this.posDataForm.controls.maxTransactionAttMode.setValue(
      pos?.additionalData?.pcsPosConfiguration?.maxTransactionAttMode ?? null
    );
    this.posDataForm.controls.maxTransactionFp.setValue(
      pos?.additionalData?.pcsPosConfiguration?.maxTransactionFp ?? null
    );

    if (duplication) {
      const virtualPosArray = this.posDataForm.get(
        POS_FORM_PARAMS.virtualPos
      ) as FormArray;
      virtualPosArray.controls.forEach((control: any) => {
        control.get(POS_FORM_PARAMS.code).setValue(null);
        control.get(POS_FORM_PARAMS.posNumber).setValue(null);
      });
    }

    setProtocolFormValues(this.posDataForm, pos);
  }

  setVPosFormArray(pos: SitePosDTO) {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    this.clearFormArray(vPosList);

    pos.virtualPos.forEach((vPos: VirtualPosDTO) => {
      vPosList.push(this.createVPosFormGroup(vPos));
      this.tabs.push("VPos");
    });
  }

  duplicate() {
    this.setFormValues(this.currentPos, true);

    this.isCreate = true;
    this.currentPos = null;
  }

  onSubmit() {
    const currentVPosForm = this.getFormArray(POS_FORM_PARAMS.virtualPos)
      .controls[this.selectedTab.value - 1];
    const deviceProxyPortControl = currentVPosForm?.get(
      POS_FORM_PARAMS.deviceProxyPort
    );
    if (
      deviceProxyPortControl?.value === null ||
      deviceProxyPortControl?.value === ""
    ) {
      deviceProxyPortControl.setValue(DEFAULT_PORT);
    }
    if (this.isCreate) {
      this.createPOSDevice();
    } else {
      this.updatePOSDevice();
    }
  }

  generatePOSDevicePayload(withVirtualPos: boolean) {
    const protocolFormGroup = this.posDataForm.get(
      POS_FORM_PARAMS.protocol
    ) as FormGroup;
    let posDevicePayload = {
      code: this.currentPos?.code ?? null,
      siteCode: this.siteCode ?? null,
      enabled: this.posDataForm.controls.enabled.value ?? false,
      posNumber: this.posDataForm.controls.posNumber.value ?? null,
      serialNumber: this.posDataForm.controls.serialNumber.value ?? null,
      description: this.posDataForm.controls.description.value ?? null,
      connectedFuelPoints:
        this.posDataForm.controls.connectedFuelPoints.value ?? null,
      connectedPeripherals:
        this.posDataForm.controls.connectedPeripherals.value ?? null,
      connectedMultimedia:
        this.posDataForm.controls.connectedMultimedia.value ?? null,
      additionalData: this.setAdditionalData(),
      protocolTypeCode:
        this.posDataForm.controls.protocolTypeCode.value ?? null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
      properties: CategoriesAndPropsUtils.setFormProperties(
        this.posDataForm.controls.posProperties.value
      ),
    };

    if (withVirtualPos) {
      posDevicePayload[POS_FORM_PARAMS.virtualPos] = this.setVPosArray();
    }

    return posDevicePayload;
  }

  createPOSDevice() {
    if (!this.posDataForm.valid) {
      this.posDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.posDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let pos = this.generatePOSDevicePayload(true);

    if (this.isCreate) {
      this.store.dispatch(createPos({ pos }));
    } else {
      this.store.dispatch(updatePos({ pos }));
    }

    this.posDataForm.markAsUntouched();
    this.posDataForm.markAsPristine();
    this.getPosPristine();
  }

  updatePOSDevice() {
    if (this.selectedTab.value === 0) {
      const tempVirtualPos = this.posDataForm.get(POS_FORM_PARAMS.virtualPos);
      this.posDataForm.removeControl(POS_FORM_PARAMS.virtualPos);

      if (!this.posDataForm.valid) {
        this.posDataForm.markAllAsTouched();
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.formInvalid"),
          type: SnackbarType.ERROR,
        });
        this.posDataForm.addControl(POS_FORM_PARAMS.virtualPos, tempVirtualPos);
        return;
      }

      if (this.posDataForm.pristine && !this.isCreate) {
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.NoChanges"),
          type: SnackbarType.WARNING,
        });
        this.posDataForm.addControl(POS_FORM_PARAMS.virtualPos, tempVirtualPos);
        return;
      }

      let pos = this.generatePOSDevicePayload(false);

      if (this.isCreate) {
        this.store.dispatch(createPos({ pos }));
      } else {
        this.store.dispatch(updatePos({ pos }));
      }

      this.posDataForm.markAsUntouched();
      this.posDataForm.markAsPristine();
      this.getPosPristine();
      this.posDataForm.addControl(POS_FORM_PARAMS.virtualPos, tempVirtualPos);
    } else {
      let currentVPosForm = this.getFormArray(POS_FORM_PARAMS.virtualPos)
        .controls[this.selectedTab.value - 1];

      if (!currentVPosForm.valid) {
        currentVPosForm.markAllAsTouched();
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.formInvalid"),
          type: SnackbarType.ERROR,
        });
        return;
      }

      if (currentVPosForm.pristine && !this.isCreate) {
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.NoChanges"),
          type: SnackbarType.WARNING,
        });
        return;
      }

      let { posTypeCode, vPosEnabled, connectedPumps, ...pos } =
        currentVPosForm.value;
      pos = {
        ...pos,
        siteCode: this.siteCode ?? null,
        protocolTypeCode: posTypeCode,
        properties: CategoriesAndPropsUtils.setFormProperties(pos.properties),
        parentCode: this.currentPos?.code ?? null,
        enabled: vPosEnabled,
        connectedFuelPoints: connectedPumps,
      };

      pos.additionalData = this.setVposAdditionalData(
        pos,
        currentVPosForm.value
      );

      if (this.isCreate) {
        this.store.dispatch(createPos({ pos }));
      } else {
        if (this.disableRemoveVPosButton()) {
          this.store.dispatch(updateVPos({ pos }));
        } else {
          this.store.dispatch(createVPos({ pos }));
        }
      }

      currentVPosForm.markAsUntouched();
      currentVPosForm.markAsPristine();
      this.getPosPristine();
    }
  }

  setVPosArray() {
    return this.posDataForm
      .get(POS_FORM_PARAMS.virtualPos)
      .value.map((vPos) => {
        const { posTypeCode, vPosEnabled, connectedPumps, ...filteredVPos } =
          vPos;
        return {
          ...filteredVPos,
          code: this.isCreate ? null : filteredVPos.code,
          siteCode: this.siteCode ?? null,
          protocolTypeCode: posTypeCode,
          properties: CategoriesAndPropsUtils.setFormProperties(
            filteredVPos.properties
          ),
          connectedFuelPoints: connectedPumps,
          enabled: vPosEnabled,
        };
      });
  }

  addTab() {
    this.addVPos();
    this.tabs.push("VPos");
    this.selectedTab.setValue(this.tabs.length - 1);
  }

  removeTab(index: number) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.posDataForm.markAsDirty();
        this.removeVPos(index - 1);
        this.tabs.splice(index, 1);
      }
    });
  }

  resetTabs() {
    this.tabs = ["POS"];
  }

  createVPosFormGroup(virtualPos?: VirtualPosDTO): FormGroup {
    const {
      code,
      posNumber,
      description,
      connectedFuelPoints,
      connectedPeripherals,
      connectedMultimedia,
      posTypeCode,
      enabled,
      additionalData,
    } = virtualPos || {};

    const formGroup = this.fb.group({
      [POS_FORM_PARAMS.code]: [code ? code : null],
      [POS_FORM_PARAMS.posNumber]: [
        posNumber || posNumber === 0 ? posNumber : null,
        [Validators.required, Validators.min(0), Validators.max(255)],
      ],
      [POS_FORM_PARAMS.description]: [
        description ? description : null,
        [Validators.required, Validators.maxLength(45)],
      ],
      [POS_FORM_PARAMS.connectedPumps]: [
        connectedFuelPoints ? connectedFuelPoints : null,
      ],
      [POS_FORM_PARAMS.connectedPeripherals]: [
        connectedPeripherals ? connectedPeripherals : null,
      ],
      [POS_FORM_PARAMS.connectedMultimedia]: [
        connectedMultimedia ? connectedMultimedia : null,
      ],
      [POS_FORM_PARAMS.posTypeCode]: posTypeCode ? posTypeCode : null,
      [POS_FORM_PARAMS.properties]: this.fb.array([]),
      [POS_FORM_PARAMS.vPosEnabled]: this.isCreate ? true : enabled ?? true,
      [POS_FORM_PARAMS.deviceProxyIp]: [
        additionalData?.deviceProxy?.ip ? additionalData?.deviceProxy.ip : null,
      ],
      [POS_FORM_PARAMS.deviceProxyPort]: [
        additionalData?.deviceProxy?.port ?? DEFAULT_PORT,
      ],
      [POS_FORM_PARAMS.printSaleReceipt]: [
        this.toBoolean(additionalData?.pcsPosConfiguration?.printSaleReceipt),
      ],
      [POS_FORM_PARAMS.showAfterSale]: [
        this.toBoolean(additionalData?.pcsPosConfiguration?.showAfterSale),
      ],
      [POS_FORM_PARAMS.showKeyboard]: [
        this.toBoolean(additionalData?.pcsPosConfiguration?.showKeyboard),
      ],
      [POS_FORM_PARAMS.printShift]: [
        this.toBoolean(additionalData?.pcsPosConfiguration?.printShift),
      ],
      [POS_FORM_PARAMS.dailyAllowsUnpaidTransactionWhenClose]: [
        this.toBoolean(
          additionalData?.pcsPosConfiguration
            ?.dailyAllowsUnpaidTransactionWhenClose
        ),
      ],
      [POS_FORM_PARAMS.additionalData]: [
        JSON.stringify(additionalData, null, "\t"),
        [FormUtils.jsonValidator],
      ],
      [POS_FORM_PARAMS.optType]: [
        additionalData?.optType ? additionalData.optType : "PEDESTAL",
      ],
      [POS_FORM_PARAMS.calculatorWebsocketUrl]: [
        additionalData?.calculatorWebsocketUrl
          ? additionalData.calculatorWebsocketUrl
          : "ws://localhost:51040",
      ],
      [POS_FORM_PARAMS.posManagerWebsocketUrl]: [
        additionalData?.posManagerWebsocketUrl
          ? additionalData.posManagerWebsocketUrl
          : "ws://localhost:51028",
      ],
      [POS_FORM_PARAMS.axonMultimediaIframe]: [
        additionalData?.axonMultimediaIframe
          ? additionalData.axonMultimediaIframe
          : false,
      ],
      [POS_FORM_PARAMS.axonMultimediaUrl]: [
        additionalData?.axonMultimediaUrl
          ? additionalData.axonMultimediaUrl
          : "http://localhost:8085",
      ],
      [POS_FORM_PARAMS.calculatorTimeout]: [
        additionalData?.calculatorTimeout
          ? additionalData.calculatorTimeout
          : 1200000,
      ],
      [POS_FORM_PARAMS.clock]: [
        additionalData?.clock || additionalData?.clock === false ? additionalData.clock : true,
      ],
      [POS_FORM_PARAMS.debug]: [
        additionalData?.debug ? additionalData.debug : false,
      ],
      [POS_FORM_PARAMS.locale]: [
        additionalData?.locale ? additionalData.locale : this.locales[0].code,
      ],
      [POS_FORM_PARAMS.productUnit]: [
        additionalData?.productUnit ? additionalData.productUnit : "L",
      ],
      [POS_FORM_PARAMS.themeId]: [
        additionalData?.themeId
          ? this.setFormTheme(additionalData.themeId)
          : this.setFormTheme("0"),
      ],
      [POS_FORM_PARAMS.background]: [
        additionalData?.background
          ? this.setFormBackground(additionalData.background)
          : this.setFormBackground("0"),
      ],
      [POS_FORM_PARAMS.title]: [
        additionalData?.title ? additionalData.title : "Fleetsys 7",
      ],
      [POS_FORM_PARAMS.video]: [
        additionalData?.video ? additionalData.video : false,
      ],
      [POS_FORM_PARAMS.welcomeMessage]: [
        additionalData?.welcomeMessage
          ? additionalData.welcomeMessage
          : "Present card or tap the screen",
      ],
      [POS_FORM_PARAMS.calculatorSides]: [
        additionalData?.calculatorSides
          ? additionalData.calculatorSides
          : this.calculatorSides[0],
      ],
    });

    const vPosDeviceProxyPortControl = formGroup.get(
      POS_FORM_PARAMS.deviceProxyPort
    );
    vPosDeviceProxyPortControl.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((value) => {
        if (value === null || value === "") {
          vPosDeviceProxyPortControl.setValue(DEFAULT_PORT, {
            emitEvent: false,
          });
        }
      });

    return formGroup;
  }

  setFormTheme(themeId: string): string {
    switch (themeId) {
      case "0":
        return "green";
      case "1":
        return "blue";
      case "2":
        return "red";
      case "3":
        return "purple";
      default:
        return "";
    }
  }

  setFormBackground(background: string): string {
    switch (background) {
      case "0":
        return "dark";
      case "1":
        return "light";
      default:
        return "";
    }
  }

  addVPos() {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    vPosList.push(this.createVPosFormGroup());
  }

  removeVPos(index: number) {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    vPosList.removeAt(index);
  }

  getFormArray(formParam: string): FormArray {
    return this.posDataForm.get(formParam) as FormArray;
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
    this.resetTabs();
  }

  clearFormControl(form: FormGroup, control: string): void {
    form.controls[control].setValue(null);
    form.markAsDirty();
  }

  getVposNr(index: number) {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    return vPosList.value[index]?.posNumber ?? "--";
  }

  getPosPristine() {
    this.getPosForm.emit(this.posDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.posDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }

  disableRemoveVPosButton() {
    if (this.isCreate || this.selectedTab.value <= 0) {
      return false;
    }

    const virtualPosArray = this.posDataForm.get(
      POS_FORM_PARAMS.virtualPos
    ).value;
    return !!virtualPosArray[this.selectedTab.value - 1]?.code;
  }

  private setAdditionalData() {
    return {
      deviceProxy: this.setAdditionalDataDeviceProxy(),
      pcsPosConfiguration: this.setAdditionalPcsPosConfiguration(),
    };
  }

  private setAdditionalDataDeviceProxy() {
    const deviceProxyIp = this.posDataForm.controls.deviceProxyIp.value;
    const deviceProxyPort = this.posDataForm.controls.deviceProxyPort.value;
    if (!deviceProxyIp && !deviceProxyPort) {
      return null;
    }
    return {
      ip: deviceProxyIp,
      port: deviceProxyPort,
    };
  }

  private setAdditionalPcsPosConfiguration() {
    const maxTransactionAttMode =
      this.posDataForm.controls.maxTransactionAttMode.value;
    const maxTransactionFp = this.posDataForm.controls.maxTransactionFp.value;
    return {
      maxTransactionAttMode: maxTransactionAttMode,
      maxTransactionFp: maxTransactionFp,
    };
  }

  private setVposAdditionalData(pos, posFormValues) {
    if (
      pos.protocolTypeCode === "PULSE_POS" ||
      pos.protocolTypeCode === "HANDHELD_POS"
    ) {
      return {
        deviceProxy: {
          ip: posFormValues.deviceProxyIp,
          port: posFormValues.deviceProxyPort,
        },
        pcsPosConfiguration: {
          printSaleReceipt:
            pos.protocolTypeCode === "PULSE_POS"
              ? posFormValues.printSaleReceipt
              : false,
          showAfterSale:
            pos.protocolTypeCode === "PULSE_POS"
              ? posFormValues.showAfterSale
              : false,
          showKeyboard:
            pos.protocolTypeCode === "PULSE_POS"
              ? posFormValues.showKeyboard
              : false,
          printShift:
            pos.protocolTypeCode === "PULSE_POS"
              ? posFormValues.printShift
              : false,
          dailyAllowsUnpaidTransactionWhenClose:
            pos.protocolTypeCode === "PULSE_POS"
              ? posFormValues.dailyAllowsUnpaidTransactionWhenClose
              : false,
        },
      };
    } else if (pos.protocolTypeCode === "OPT_FLEET") {
      return {
        deviceProxy: {
          ip: posFormValues.deviceProxyIp,
          port: posFormValues.deviceProxyPort,
        },
        optType: posFormValues.optType,
        calculatorWebsocketUrl: posFormValues.calculatorWebsocketUrl,
        posManagerWebsocketUrl: posFormValues.posManagerWebsocketUrl,
        axonMultimediaIframe: posFormValues.axonMultimediaIframe,
        axonMultimediaUrl: posFormValues.axonMultimediaUrl,
        calculatorTimeout: posFormValues.calculatorTimeout,
        clock: posFormValues.clock,
        debug: posFormValues.debug,
        locale: posFormValues.locale,
        productUnit: posFormValues.productUnit,
        title: posFormValues.title,
        video: posFormValues.video,
        welcomeMessage: posFormValues.welcomeMessage,
        themeId: this.setThemeId(posFormValues.themeId),
        background: this.setBackground(posFormValues.background),
        calculatorSides: posFormValues.calculatorSides,
      };
    } else {
      return pos.additionalData ? JSON.parse(pos.additionalData) : {};
    }
  }

  setThemeId(background: string): string {
    switch (background) {
      case "green":
        return "0";
      case "blue":
        return "1";
      case "red":
        return "2";
      case "purple":
        return "3";
      default:
        return "0";
    }
  }

  setBackground(themeId: string): string {
    switch (themeId) {
      case "dark":
        return "0";
      case "light":
        return "1";
      default:
        return "0";
    }
  }

  private toBoolean(value: any): boolean {
    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }
    return !!value;
  }
}
